import React, { useState, PropsWithChildren, useContext } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import { Typography } from '@material-ui/core';
import { _, useTranslation } from '~/dependencies';
import { log } from '~/helpers';
import * as actions from '~/store/actions';
import api from '~/services';
import { AgentStatusContext } from '~/providers/AgentStatusProvider';
import {
  searchIn,
  filterFavoriteExtensions,
  filterUnfavoriteExtensions,
} from './utils';
import List from './List';
import { Container, CenterMessage } from './styles';

type Props = PropsWithChildren<{
  searchFor?: string;
  maxHeight?: string;
  agentNameMaxLength?: number;
}>;

const AgentsList = ({
  searchFor = '',
  maxHeight,
  children,
  ...rest
}: Props) => {
  // HOOK's
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // CONTEXT'S
  const { refreshAgents } = useContext(AgentStatusContext);

  const [loading, setLoading] = useState(false);

  const userExtension = useSelector(
    (state: RootStateOrAny) => state.phone?.sipCredentials?.extension
  );

  const favoriteAgents = useSelector(
    (state: RootStateOrAny) => state.phone?.favoriteAgents
  );

  const favoriteExtensions = searchIn(
    useSelector(filterFavoriteExtensions),
    searchFor
  );
  const unfavoriteExtensions = searchIn(
    useSelector(filterUnfavoriteExtensions),
    searchFor
  );

  async function updateFavoriteAgents(agents = '') {
    const requestPayload = {
      extension: String(userExtension),
      agents,
    };

    try {
      await api.users.post.updateFavoriteAgents(requestPayload);
      dispatch(actions.fetchFavoriteAgents());
      // dispatch(actions.refreshWebSocket());
      refreshAgents();
    } catch (err) {
      log.error(`Failed to update favorite agents: ${err}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }

  function addFavoriteAgent(extension: number) {
    if (extension) {
      setLoading(true);

      const findIndex = favoriteAgents.findIndex(
        (ext: string) => ext === String(extension)
      );

      let favorites = [];

      // If externsion is already in favorites, then remove it.
      if (findIndex !== -1) {
        favorites = favoriteAgents.filter(
          (ext: string) => ext !== String(extension)
        );
      } else {
        // If externsion is NOT in favorites, then add it.
        favorites = [...favoriteAgents, String(extension)];
      }

      const extensionsInString = favorites.reduce(
        (prev: string, next: string) => `${prev + next},`,
        []
      );

      if (extensionsInString.length) {
        updateFavoriteAgents(
          extensionsInString.slice(0, extensionsInString.length - 1)
        );
      } else {
        updateFavoriteAgents();
      }
    }
  }

  return (
    <Container maxHeight={maxHeight}>
      {_.isEmpty(favoriteExtensions) && _.isEmpty(unfavoriteExtensions) ? (
        <CenterMessage>
          <Typography>
            {t('screens.teams.components.lists.membersNotFound')}
          </Typography>
        </CenterMessage>
      ) : (
        <>
          <List
            data-test-id="favorites-list"
            title={t('screens.teams.favorites.title')}
            agents={favoriteExtensions}
            actions={children}
            favorite
            loading={loading}
            addFavoriteAgent={addFavoriteAgent}
            {...rest}
          />

          <List
            data-test-id="extensions-list"
            title={t('screens.teams.extensions.title')}
            agents={unfavoriteExtensions}
            actions={children}
            loading={loading}
            addFavoriteAgent={addFavoriteAgent}
            {...rest}
          />
        </>
      )}
    </Container>
  );
};

AgentsList.defaultProps = {
  searchFor: '',
  maxHeight: 'auto',
  agentNameMaxLength: 25,
};

export default AgentsList;
